import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { Button } from '@shared/components/ui/button';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuildPendingAccounting } from '../../hooks/useBuildPendingAccounting';

function getBuildPendingErrorDescription(error: unknown, t: (key: string) => string): string {
  const axiosPayload = error as { response?: { data?: { error?: { error_code?: string; message?: string } } } };
  const code = axiosPayload?.response?.data?.error?.error_code;
  if (code) {
    const translated = t(`build_pending.errors.${code}`);
    if (translated !== `build_pending.errors.${code}`) {
      return translated;
    }
  }
  return getErrorMessage(error);
}

export const BuildPendingAccountingButton = () => {
  const { t } = useTranslation('accountingSyncModule');
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const buildPending = useBuildPendingAccounting();
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  if (!hasPermission('wallets.manage')) {
    return null;
  }

  const runBuildPending = () => {
    buildPending.mutate(
      {},
      {
        onSuccess: (data) => {
          toast({
            title: t('build_pending.success_title'),
            description:
              data.message ||
              t('build_pending.success_description', { posted: data.posted, skipped: data.skipped_empty }),
          });
        },
        onError: (error) => {
          toast({
            title: t('build_pending.error_title'),
            description: getBuildPendingErrorDescription(error, t),
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleClick = () => {
    confirmModalRef.current?.open(
      runBuildPending,
      undefined,
      t('build_pending.confirm.title'),
      t('build_pending.confirm.message'),
      t('build_pending.confirm.submit'),
      t('build_pending.confirm.cancel'),
    );
  };

  return (
    <>
      <Button
        type="button"
        size="default"
        variant="primary"
        onClick={handleClick}
        disabled={buildPending.isPending}
        className="gap-2"
        data-testid="accounting-sync-build-pending"
      >
        <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
        {buildPending.isPending ? t('build_pending.running') : t('build_pending.button')}
      </Button>

      <ConfirmModal ref={confirmModalRef} />
    </>
  );
};
